// client/src/services/userAuthService.js
import axios from "axios";
import i18n from "../locales/i18n";

/**
 * User Authentication Service
 *
 * Dedicated service for user authentication, registration, and profile management.
 * Completely separate from admin authentication for clean code organization.
 *
 * Features:
 * - User registration and login
 * - Automatic token refresh
 * - "Remember Me" functionality
 * - Profile management
 * - Secure token storage
 *
 * UPDATED: Now includes email verification workflow support
 */

// Token storage keys
const ACCESS_TOKEN_KEY = "telo_user_access_token";
const REFRESH_TOKEN_KEY = "telo_user_refresh_token";
const USER_DATA_KEY = "telo_user_data";
const REMEMBER_ME_KEY = "telo_remember_me";

/**
 * Token Management Functions
 */
const tokenManager = {
    // Get access token from storage
    getAccessToken: () => {
        return (
            localStorage.getItem(ACCESS_TOKEN_KEY) ||
            sessionStorage.getItem(ACCESS_TOKEN_KEY)
        );
    },

    // Get refresh token from storage
    getRefreshToken: () => {
        return (
            localStorage.getItem(REFRESH_TOKEN_KEY) ||
            sessionStorage.getItem(REFRESH_TOKEN_KEY)
        );
    },

    // Store tokens (respects "Remember Me" setting)
    setTokens: (accessToken, refreshToken, rememberMe = true) => {
        if (rememberMe) {
            // Store in localStorage for persistence across browser sessions
            localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
            localStorage.setItem(REMEMBER_ME_KEY, "true");
        } else {
            // Store in sessionStorage for current session only
            sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
            sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
            localStorage.setItem(REMEMBER_ME_KEY, "false");
        }
    },

    // Store user data
    setUserData: (userData) => {
        const storage =
            localStorage.getItem(REMEMBER_ME_KEY) === "true"
                ? localStorage
                : sessionStorage;
        storage.setItem(USER_DATA_KEY, JSON.stringify(userData));
    },

    // Get user data
    getUserData: () => {
        const stored =
            localStorage.getItem(USER_DATA_KEY) ||
            sessionStorage.getItem(USER_DATA_KEY);
        return stored ? JSON.parse(stored) : null;
    },

    // Clear all tokens and user data
    clearTokens: () => {
        localStorage.removeItem(ACCESS_TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        localStorage.removeItem(USER_DATA_KEY);
        localStorage.removeItem(REMEMBER_ME_KEY);
        sessionStorage.removeItem(ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(REFRESH_TOKEN_KEY);
        sessionStorage.removeItem(USER_DATA_KEY);
    },

    // Check if user wants to be remembered
    shouldRemember: () => {
        return localStorage.getItem(REMEMBER_ME_KEY) === "true";
    },
};

/**
 * Create axios instance for user API requests
 */
const createUserAPI = () => {
    const userAPI = axios.create({
        baseURL: process.env.REACT_APP_API_URL || "http://localhost:3000/api",
        headers: {
            "Content-Type": "application/json",
        },
    });

    // Request interceptor - automatically add access token to requests
    userAPI.interceptors.request.use(
        (config) => {
            const token = tokenManager.getAccessToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
            return config;
        },
        (error) => Promise.reject(error)
    );

    // Response interceptor - handle automatic token refresh and email verification
    userAPI.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // NEW: Handle email verification errors
            // BUT NOT for login requests - let the login function handle those
            if (
                error.response?.status === 403 &&
                error.response?.data?.code === "EMAIL_NOT_VERIFIED" &&
                !originalRequest.url.includes('/auth/login')
            ) {
                // User needs to verify email - clear tokens and emit event
                tokenManager.clearTokens();
                window.dispatchEvent(
                    new CustomEvent("auth:email-not-verified", {
                        detail: error.response.data,
                    })
                );
                return Promise.reject({
                    ...error.response.data,
                    requiresVerification: true,
                });
            }

            // If access token expired and we haven't already tried to refresh
            if (
                error.response?.status === 401 &&
                error.response?.data?.code === "TOKEN_EXPIRED" &&
                !originalRequest._retry
            ) {
                originalRequest._retry = true;

                try {
                    // Try to refresh the token
                    const refreshToken = tokenManager.getRefreshToken();
                    if (!refreshToken) {
                        throw new Error("No refresh token available");
                    }

                    const response = await axios.post(
                        `${
                            process.env.REACT_APP_API_URL ||
                            "http://localhost:3000/api"
                        }/auth/refresh`,
                        { refreshToken }
                    );

                    const { accessToken, refreshToken: newRefreshToken } =
                        response.data;

                    // Store new tokens
                    tokenManager.setTokens(
                        accessToken,
                        newRefreshToken,
                        tokenManager.shouldRemember()
                    );

                    // Retry the original request with new token
                    originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                    return userAPI(originalRequest);
                } catch (refreshError) {
                    // Refresh failed - user needs to login again
                    tokenManager.clearTokens();

                    // Emit custom event for authentication context to handle
                    window.dispatchEvent(new CustomEvent("auth:token-expired"));

                    return Promise.reject(refreshError);
                }
            }

            return Promise.reject(error);
        }
    );

    return userAPI;
};

// Create user API instance
const userAPI = createUserAPI();

// Normalize user payload from API to ensure consistent shape
const normalizeUser = (u) => {
    if (!u || typeof u !== "object") return u;
    const user = { ...u };
    user._id = user._id || user.id || user.userId || null;
    if (!Array.isArray(user.favorites)) {
        user.favorites = user.favorites ? [].concat(user.favorites) : [];
    }
    if (typeof user.favoritesCount !== "number") {
        user.favoritesCount = Array.isArray(user.favorites)
            ? user.favorites.length
            : 0;
    }
    return user;
};

/**
 * User Authentication Service Methods
 */
const userAuthService = {
    /**
     * Register new user account
     * UPDATED: Now handles email verification requirement
     */
    register: async (userData) => {
        try {
            const currentLang = ((i18n?.language || 'ka').toLowerCase().split('-')[0] === 'en') ? 'en' : 'ka';
            const payload = { ...userData, lang: currentLang };
            const response = await userAPI.post("/auth/register", payload);
            const data = response.data;

            // NEW: Check if registration requires email verification
            if (data.requiresVerification) {
                // Registration successful but requires email verification
                // Do NOT store tokens yet
                return {
                    success: true,
                    user: data.user,
                    message: data.message,
                    requiresVerification: true,
                    emailSendFailed: data.emailSendFailed || false,
                };
            } else {
                // Legacy path: immediate login (shouldn't happen with new backend)
                const accessToken = data.accessToken;
                const refreshToken = data.refreshToken;
                const user = normalizeUser(data.user);

                tokenManager.setTokens(accessToken, refreshToken, true);
                tokenManager.setUserData(user);

                return {
                    success: true,
                    user,
                    message: data.message,
                    requiresVerification: false,
                };
            }
        } catch (error) {
            throw new Error(JSON.stringify({
                message: error.response?.data?.error || "Registration failed",
                details: error.response?.data?.details || [],
                code: error.response?.data?.code || "REGISTRATION_ERROR",
            }));
        }
    },

    /**
     * Login existing user
     * UPDATED: Now handles email verification requirement
     */
    login: async (credentials, rememberMe = true) => {
        try {
            const response = await userAPI.post("/auth/login", credentials);
            const data = response.data;

            // NEW: Check if login requires email verification
            if (data.requiresVerification) {
                // Credentials valid but email not verified
                return {
                    success: false,
                    message: data.error || "Please verify your email address",
                    requiresVerification: true,
                    email: data.email,
                    code: data.code,
                };
            } else {
                // Normal login flow
                const accessToken = data.accessToken;
                const refreshToken = data.refreshToken;
                const deletionCancelled = data.deletionCancelled;
                const user = normalizeUser(data.user);

                // Store tokens and user data with remember me preference
                tokenManager.setTokens(accessToken, refreshToken, rememberMe);
                tokenManager.setUserData(user);

                return {
                    success: true,
                    user,
                    message: data.message,
                    requiresVerification: false,
                    deletionCancelled: deletionCancelled || false,
                };
            }
        } catch (error) {
            // Handle email verification errors from backend
            if (
                error.response?.status === 403 &&
                error.response?.data?.code === "EMAIL_NOT_VERIFIED"
            ) {
                return {
                    success: false,
                    message: error.response.data.error,
                    requiresVerification: true,
                    email: error.response.data.email,
                    code: error.response.data.code,
                };
            }

            throw new Error(JSON.stringify({
                message: error.response?.data?.error || "Login failed",
                details: error.response?.data?.details || [],
                code: error.response?.data?.code || "LOGIN_ERROR",
            }));
        }
    },

    /**
     * NEW: Google OAuth login
     * Handles Google authentication with automatic email verification
     */
    loginWithGoogle: async (idToken) => {
        try {
            const response = await userAPI.post("/auth/google/callback", { idToken });
            const data = response.data;

            // Google users are auto-verified, so always successful login
            const accessToken = data.accessToken;
            const refreshToken = data.refreshToken;
            const deletionCancelled = data.deletionCancelled;
            const accountLinked = data.accountLinked;
            const user = normalizeUser(data.user);

            // Store tokens and user data (always remember Google logins)
            tokenManager.setTokens(accessToken, refreshToken, true);
            tokenManager.setUserData(user);

            return {
                success: true,
                user,
                message: data.message || "Successfully signed in with Google",
                requiresVerification: false,
                deletionCancelled: deletionCancelled || false,
                accountLinked: accountLinked || false,
            };
        } catch (error) {
            const status = error.response?.status;
            const code = error.response?.data?.code;
            if (status === 409 && code === "LINK_CONFIRM_REQUIRED") {
                // Return structured result for UI to prompt password-only linking
                return {
                    success: false,
                    requireLinkConfirm: true,
                    email: error.response?.data?.email,
                    linkToken: error.response?.data?.linkToken,
                    expiresAt: error.response?.data?.expiresAt,
                    message: error.response?.data?.message || "Google sign-in requires confirmation",
                };
            }
            throw new Error(JSON.stringify({
                message: error.response?.data?.error || "Google sign-in failed",
                details: error.response?.data?.details || [],
                code: code || "GOOGLE_LOGIN_ERROR",
            }));
        }
    },

    /**
     * Confirm linking Google to an existing local account
     */
    linkGoogleAccount: async (linkToken, password) => {
        const response = await userAPI.post("/auth/link/google", { linkToken, password });
        const accessToken = response.data.accessToken;
        const refreshToken = response.data.refreshToken;
        const user = normalizeUser(response.data.user);
        // Always remember linked Google sessions
        tokenManager.setTokens(accessToken, refreshToken, true);
        tokenManager.setUserData(user);
        return { success: true, user };
    },

    /**
     * Logout user
     */
    logout: async () => {
        try {
            // Call backend logout endpoint
            await userAPI.post("/auth/logout");
        } catch (error) {
            // Even if backend logout fails, clear local tokens
            console.warn("Logout API call failed:", error.message);
        } finally {
            // Always clear local storage
            tokenManager.clearTokens();
        }
    },

    /**
     * Get current user profile
     * UPDATED: Now handles email verification errors
     */
    getCurrentUser: async () => {
        try {
            const response = await userAPI.get("/auth/me");
            const user = normalizeUser(response.data.user);

            // Update stored user data
            tokenManager.setUserData(user);

            return user;
        } catch (error) {
            // Handle email verification errors
            if (
                error.response?.status === 403 &&
                error.response?.data?.code === "EMAIL_NOT_VERIFIED"
            ) {
                const err = new Error(error.response.data.error);
                err.code = "EMAIL_NOT_VERIFIED";
                err.requiresVerification = true;
                throw err;
            }

            const err = new Error(
                error.response?.data?.error || "Failed to get user profile"
            );
            err.code = error.response?.data?.code || "PROFILE_ERROR";
            throw err;
        }
    },

    /**
     * Update user profile
     */
    updateProfile: async (profileData) => {
        try {
            const response = await userAPI.put("/auth/profile", profileData);
            const user = normalizeUser(response.data.user);

            // Update stored user data
            tokenManager.setUserData(user);

            return {
                success: true,
                user,
                message: response.data.message,
            };
        } catch (error) {
            const err = new Error(error.response?.data?.error || "Profile update failed");
            err.details = error.response?.data?.details || [];
            err.code = error.response?.data?.code || "PROFILE_UPDATE_ERROR";
            throw err;
        }
    },

    /**
     * Refresh access token
     * UPDATED: Now handles email verification during refresh
     */
    refreshToken: async () => {
        try {
            const refreshToken = tokenManager.getRefreshToken();
            if (!refreshToken) {
                throw new Error("No refresh token available");
            }

            const response = await axios.post(
                `${
                    process.env.REACT_APP_API_URL || "http://localhost:3000/api"
                }/auth/refresh`,
                { refreshToken }
            );

            const { accessToken, refreshToken: newRefreshToken, user } = response.data;

            // Store new tokens
            tokenManager.setTokens(
                accessToken,
                newRefreshToken,
                tokenManager.shouldRemember()
            );

            // Update user data if provided
            if (user) {
                const norm = normalizeUser(user);
                tokenManager.setUserData(norm);
                return { accessToken, refreshToken: newRefreshToken, user: norm };
            }

            return { accessToken, refreshToken: newRefreshToken, user };
        } catch (error) {
            // Handle email verification errors during refresh
            if (
                error.response?.status === 403 &&
                error.response?.data?.code === "EMAIL_NOT_VERIFIED"
            ) {
                tokenManager.clearTokens();
                const err = new Error("Email verification required");
                err.code = "EMAIL_NOT_VERIFIED";
                err.requiresVerification = true;
                throw err;
            }

            tokenManager.clearTokens();
            const err = new Error("Session expired. Please login again.");
            err.code = "REFRESH_TOKEN_EXPIRED";
            throw err;
        }
    },

    /**
     * Check if user is currently authenticated
     * UPDATED: Now checks email verification status too
     */
    isAuthenticated: () => {
        const token = tokenManager.getAccessToken();
        const userData = tokenManager.getUserData();

        // NEW: Only consider authenticated if user has token, data, AND is email verified
        return !!(token && userData && userData.isEmailVerified);
    },

    /**
     * NEW: Check if user has tokens but needs email verification
     */
    hasTokensButUnverified: () => {
        const token = tokenManager.getAccessToken();
        const userData = tokenManager.getUserData();

        return !!(token && userData && !userData.isEmailVerified);
    },

    /**
     * Get current user data from storage (without API call)
     */
    getCurrentUserFromStorage: () => {
        return tokenManager.getUserData();
    },

    /**
     * Clear user session (for forced logout)
     */
    clearSession: () => {
        tokenManager.clearTokens();
    },

    /**
     * NEW: Handle successful email verification
     * Called when user completes email verification
     */
    handleEmailVerification: (user, tokens) => {
        const rememberMe = tokenManager.shouldRemember();

        // Store new tokens and updated user data
        if (tokens && tokens.accessToken && tokens.refreshToken) {
            tokenManager.setTokens(
                tokens.accessToken,
                tokens.refreshToken,
                rememberMe
            );
        }

        const norm = normalizeUser(user);
        tokenManager.setUserData(norm);

        return {
            success: true,
            user: norm,
            message: "Email verified successfully",
        };
    },

    /**
     * NEW: Get user's email for verification purposes
     */
    getUserEmail: () => {
        const userData = tokenManager.getUserData();
        return userData?.email || null;
    },

    /**
     * NEW: Check if user needs email verification
     */
    needsEmailVerification: () => {
        const userData = tokenManager.getUserData();
        return userData && !userData.isEmailVerified;
    },

    /**
     * NEW: Request password reset
     * Phase 4B: Frontend password reset functionality
     */
    requestPasswordReset: async (email) => {
        try {
            const currentLang = ((i18n?.language || 'ka').toLowerCase().split('-')[0] === 'en') ? 'en' : 'ka';
            const response = await userAPI.post("/auth/forgot-password", { email, lang: currentLang });
            
            return {
                success: true,
                message: response.data.message,
                email: response.data.email
            };
        } catch (error) {
            // Handle rate limiting
            if (error.response?.status === 429) {
                const err = new Error(error.response.data.error);
                err.code = "RATE_LIMIT_EXCEEDED";
                err.retryAfter = error.response.data.retryAfter;
                throw err;
            }
            
            const err = new Error(error.response?.data?.error || "Failed to send password reset email");
            err.code = error.response?.data?.code || "PASSWORD_RESET_ERROR";
            throw err;
        }
    },

    /**
     * NEW: Reset password using token
     * Phase 4B: Complete password reset process
     */
    resetPassword: async (token, newPassword, confirmPassword) => {
        try {
            const response = await userAPI.post(`/auth/reset-password/${token}`, {
                newPassword,
                confirmPassword
            });
            
            return {
                success: true,
                message: response.data.message,
                email: response.data.email
            };
        } catch (error) {
            // Handle various reset errors
            if (error.response?.status === 400) {
                const errorCode = error.response.data.code;
                
                if (errorCode === "INVALID_RESET_TOKEN" || errorCode === "EXPIRED_RESET_TOKEN") {
                    const err = new Error(error.response.data.error);
                    err.code = errorCode;
                    err.expired = true;
                    throw err;
                }
                
                if (errorCode === "VALIDATION_ERROR") {
                    const err = new Error(error.response.data.error);
                    err.details = error.response.data.details || [];
                    err.code = errorCode;
                    throw err;
                }
            }
            
            const err = new Error(error.response?.data?.error || "Failed to reset password");
            err.code = error.response?.data?.code || "PASSWORD_RESET_ERROR";
            throw err;
        }
    },

    /**
     * NEW: Change email address
     * Initiates email change with verification
     */
    changeEmail: async (newEmail) => {
        try {
            const response = await userAPI.post("/auth/change-email", { newEmail });
            
            return {
                success: true,
                message: response.data.message,
                newEmail: response.data.newEmail
            };
        } catch (error) {
            // Handle specific email change errors
            if (error.response?.status === 400) {
                const errorCode = error.response.data.code;
                
                if (errorCode === "SAME_EMAIL") {
                    const err = new Error("New email address is the same as your current email");
                    err.code = errorCode;
                    throw err;
                } else if (errorCode === "PENDING_EMAIL_CHANGE_EXISTS") {
                    const err = new Error(error.response.data.error);
                    err.code = errorCode;
                    err.pendingEmail = error.response.data.pendingEmail;
                    throw err;
                }
            } else if (error.response?.status === 409) {
                const err = new Error("This email address is already associated with another account");
                err.code = "EMAIL_ALREADY_EXISTS";
                throw err;
            } else if (error.response?.status === 429) {
                const err = new Error(error.response.data.error);
                err.code = "RATE_LIMIT_EXCEEDED";
                err.retryAfter = error.response.data.retryAfter;
                throw err;
            }

            const err = new Error(error.response?.data?.error || "Failed to initiate email change");
            err.code = error.response?.data?.code || "EMAIL_CHANGE_ERROR";
            err.details = error.response?.data?.details || [];
            throw err;
        }
    },

    /**
     * NEW: Change user password
     * Changes password with current password verification
     */
    changePassword: async (passwordData) => {
        try {
            const response = await userAPI.post("/auth/change-password", passwordData);
            
            return {
                success: true,
                message: response.data.message
            };
        } catch (error) {
            // Handle specific password change errors
            if (error.response?.status === 400) {
                const errorCode = error.response.data.code;
                
                if (errorCode === "PASSWORD_MISMATCH") {
                    const err = new Error("New password and confirmation do not match");
                    err.code = errorCode;
                    err.field = "confirmPassword";
                    throw err;
                } else if (errorCode === "PASSWORD_TOO_SHORT") {
                    const err = new Error("Password must be at least 8 characters long");
                    err.code = errorCode;
                    err.field = "newPassword";
                    throw err;
                } else if (errorCode === "PASSWORD_TOO_WEAK") {
                    const err = new Error("Password must contain uppercase, lowercase and number");
                    err.code = errorCode;
                    err.field = "newPassword";
                    throw err;
                } else if (errorCode === "SAME_PASSWORD") {
                    const err = new Error("New password must be different from current password");
                    err.code = errorCode;
                    err.field = "newPassword";
                    throw err;
                }
            } else if (error.response?.status === 401) {
                const err = new Error("Current password is incorrect");
                err.code = "INVALID_CURRENT_PASSWORD";
                err.field = "currentPassword";
                throw err;
            }

            const err = new Error(error.response?.data?.error || "Failed to change password");
            err.code = error.response?.data?.code || "PASSWORD_CHANGE_ERROR";
            throw err;
        }
    },

    /**
     * NEW: Delete user account (Schedule for delayed deletion)
     * Schedules the user account for delayed deletion and logs them out immediately
     */
    deleteAccount: async (confirmationText = "DELETE") => {
        try {
            const response = await userAPI.delete("/auth/account", {
                data: { confirmationText }
            });
            
            const data = response.data;
            
            // Handle delayed deletion scheduling
            if (data.scheduledDeletion) {
                // Clear tokens immediately as instructed by server
                if (data.forceLogout) {
                    tokenManager.clearTokens();
                }
                
                return {
                    success: true,
                    message: data.message,
                    scheduledDeletion: true,
                    scheduledFor: data.scheduledFor,
                    delayDays: data.delayDays,
                    canCancelBy: data.canCancelBy,
                    forceLogout: data.forceLogout
                };
            } else {
                // Fallback for immediate deletion (shouldn't happen with new system)
                tokenManager.clearTokens();
                return {
                    success: true,
                    message: data.message,
                    scheduledDeletion: false
                };
            }
        } catch (error) {
            // Handle already scheduled deletion
            if (error.response?.status === 400 && error.response?.data?.code === "DELETION_ALREADY_SCHEDULED") {
                return {
                    success: false,
                    message: error.response.data.message,
                    code: "DELETION_ALREADY_SCHEDULED",
                    alreadyScheduled: true,
                    scheduledFor: error.response.data.scheduledFor,
                    remainingDays: error.response.data.remainingDays
                };
            }
            
            if (error.response?.status === 400) {
                const err = new Error("Account deletion requires typing 'DELETE' as confirmation");
                err.code = "INVALID_CONFIRMATION";
                throw err;
            }

            const err = new Error(error.response?.data?.error || "Failed to schedule account deletion");
            err.code = error.response?.data?.code || "DELETE_ACCOUNT_ERROR";
            throw err;
        }
    },

    /**
     * NEW: Validate email format
     */
    isValidEmail: (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email?.trim());
    },

    /**
     * Token management utilities (exported for context use)
     */
    tokenManager,

    // Export userAPI for direct access to axios instance
    userAPI,

    // NEW: Export token management functions for external use
    setTokens: tokenManager.setTokens,
    setUserData: tokenManager.setUserData,
    clearTokens: tokenManager.clearTokens,
};

export default userAuthService;
